<template>
  <div id="app-container">
    <AppTopBar
      :username="userInfo.username || 'admin'"
      :show-admin="userInfo.rol === 'admin' || userInfo.rol === 'superadmin'"
      :show-logout="true"
      :show-vista-toggle="true"
      :vista-actual="vistaActual"
      :conteo-marketplace="conteoVistas.marketplace"
      :conteo-web="conteoVistas.web"
      @logout="logout"
      @change-vista="cambiarVista"
    />

    <div class="view-content">
      <div class="view-header">
        <span class="view-kicker">ANÁLISIS</span>
        <h1 class="view-title">Inteligencia Estratégica</h1>
        <span class="view-meta">Posicionamiento, agresividad y perfiles de competencia</span>
      </div>

      <div class="strategic-analysis-section">
        <MatrizPosicionamiento :matriz="matrizData" />
        <IndiceAgresividad :agresividad="agresividadData" />
      </div>

      <PerfilesCompetidores :perfiles="perfilesData" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

import AppTopBar from '../components/AppTopBar.vue';
import MatrizPosicionamiento from '../components/MatrizPosicionamiento.vue';
import IndiceAgresividad from '../components/IndiceAgresividad.vue';
import PerfilesCompetidores from '../components/PerfilesCompetidores.vue';

const router = useRouter();
const API_BASE_URL = 'https://dash-visaovip.kompara.tech/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('token_precios');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const userInfo = ref({ username: '', rol: '' });
const vistaActual = ref(localStorage.getItem('vista_actual') || 'web');
const conteoVistas = ref({ web: 0, marketplace: 0 });

const matrizData = ref([]);
const agresividadData = ref([]);
const perfilesData = ref([]);

const cambiarVista = (v) => {
  vistaActual.value = v;
  localStorage.setItem('vista_actual', v);
};

const logout = () => {
  localStorage.removeItem('token_precios');
  router.push('/');
  window.location.reload();
};

const loadMatriz = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/estrategia/matriz-posicionamiento`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    matrizData.value = res.data;
  } catch (e) { console.error(e); }
};

const loadAgresividad = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/estrategia/indice-agresividad`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    agresividadData.value = res.data;
  } catch (e) { console.error(e); }
};

const loadPerfiles = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/estrategia/perfiles-competidores`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    perfilesData.value = res.data;
  } catch (e) { console.error(e); }
};

const loadConteo = async () => {
  for (const v of ['web', 'marketplace']) {
    try {
      const res = await axios.get(`${API_BASE_URL}/kpis/overview`, {
        ...getAuthHeaders(), params: { vista: v }
      });
      conteoVistas.value[v] = res.data.competitive_products_count || 0;
    } catch (e) { /* ignore */ }
  }
};

const loadAll = async () => {
  await Promise.all([loadMatriz(), loadAgresividad(), loadPerfiles()]);
};

watch(vistaActual, loadAll);

onMounted(async () => {
  const token = localStorage.getItem('token_precios');
  if (!token) { router.push('/'); return; }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    userInfo.value = { username: payload.sub || 'admin', rol: payload.rol || 'admin' };
  } catch { /* ignore */ }
  await loadAll();
  loadConteo();
});
</script>

<style scoped>
#app-container { min-height: 100vh; background: var(--color-background); color: var(--color-text); }
.view-content {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 1800px;
  margin: 0 auto;
}
.view-header {
  padding: 4px 0 14px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 6px;
}
.view-kicker {
  display: block;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.view-title {
  margin: 4px 0 4px;
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
}
.view-meta {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  letter-spacing: 0.03em;
}
.strategic-analysis-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
@media (max-width: 1100px) {
  .strategic-analysis-section { grid-template-columns: 1fr; }
}
</style>